import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare, ArrowDown, MoreHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ua = () => window.navigator.userAgent;
const isIOSDevice = () => /iphone|ipad|ipod/i.test(ua()) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandaloneApp = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
export const openInstall = () => window.dispatchEvent(new Event("xmd-install"));

const IosSteps = ({ onClose }) => {
  const isIPhone = /iphone|ipod/i.test(ua());
  if (!isIOSDevice()) return <OtherSteps onClose={onClose} />;
  const isChrome = /crios|edgios|fxios/i.test(ua());
  const shareWhere = isChrome ? "at the top-right of the address bar" : isIPhone ? "in the bar at the bottom of Safari" : "at the top-right of Safari";
  const steps = [
    { icon: Share, text: <>Tap the <b className="text-white">Share</b> button {shareWhere}.</> },
    { icon: MoreHorizontal, text: <>Scroll down the menu (tap <b className="text-white">More</b> if you don't see it).</> },
    { icon: PlusSquare, text: <>Tap <b className="text-white">Add to Home Screen</b>, then <b className="text-white">Add</b>.</> },
  ];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="ios-install-guide">
      <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-[#111] border border-[#D4AF37]/30 rounded-2xl p-6 mb-16 sm:mb-0">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display text-lg text-white">Install on iPhone / iPad</p>
          <button onClick={onClose} className="text-white/50 p-1" data-testid="ios-guide-close"><X size={18} /></button>
        </div>
        <ol className="space-y-4">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="h-8 w-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                <s.icon size={15} className="text-[#D4AF37]" />
              </span>
              <p className="text-white/70 text-sm pt-1">{s.text}</p>
            </li>
          ))}
        </ol>
        <p className="text-white/40 text-xs mt-5">Apple only allows installing from the Share menu — the XMD app will then open full-screen from your home screen.</p>
        <button onClick={onClose} className="btn-gold w-full rounded-full py-3 mt-5 text-sm font-mono-label uppercase" data-testid="ios-guide-ok">Got it</button>
      </motion.div>
      {isIPhone && !isChrome && (
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.4 }} className="fixed bottom-2 left-1/2 -translate-x-1/2 text-[#D4AF37] pointer-events-none">
          <ArrowDown size={34} />
        </motion.div>
      )}
    </motion.div>
  );
};

const OtherSteps = ({ onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="other-install-guide">
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#111] border border-[#D4AF37]/30 rounded-2xl p-6">
      <p className="font-display text-lg text-white mb-3">Install XMD Productions</p>
      <p className="text-white/70 text-sm">Open your browser menu (<b className="text-white">⋮</b> or the install icon in the address bar) and choose <b className="text-white">Install app</b> or <b className="text-white">Add to Home screen</b>.</p>
      <p className="text-white/40 text-xs mt-3">Works in Chrome, Edge and Samsung Internet.</p>
      <button onClick={onClose} className="btn-gold w-full rounded-full py-3 mt-5 text-sm font-mono-label uppercase" data-testid="other-guide-ok">Got it</button>
    </div>
  </motion.div>
);

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    if (isStandaloneApp()) return;
    const dismissed = !!localStorage.getItem("xmd_install_dismissed");
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); if (!dismissed) setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    setIos(isIOSDevice());
    let t;
    if (isIOSDevice() && !dismissed) t = setTimeout(() => setShow(true), 2500);
    return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, []);

  useEffect(() => {
    const onOpen = () => { setShow(false); install(); };
    window.addEventListener("xmd-install", onOpen);
    return () => window.removeEventListener("xmd-install", onOpen);
  });

  const dismiss = () => { setShow(false); setGuide(false); localStorage.setItem("xmd_install_dismissed", "1"); };
  const install = async () => {
    if (ios || !deferred) { setGuide(true); return; }
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && !guide && (
        <motion.div key="bar" initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", damping: 22 }} className="fixed bottom-4 inset-x-4 z-[60] md:left-auto md:right-6 md:w-96" data-testid="install-prompt">
          <div className="glass rounded-2xl p-4 flex items-center gap-4">
            <img src="/icon-192.png" alt="XMD" className="h-12 w-12 rounded-xl border border-white/10" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-white text-sm">Install XMD Productions</p>
              <p className="text-white/60 text-xs mt-0.5">Add to your home screen for instant access.</p>
            </div>
            <button onClick={install} data-testid="install-accept-btn"
              className="btn-gold rounded-full px-4 py-2 text-xs uppercase tracking-wide font-mono-label flex items-center gap-1">
              <Download size={14} /> Install
            </button>
            <button onClick={dismiss} data-testid="install-dismiss-btn" className="text-white/40 hover:text-white p-1"><X size={18} /></button>
          </div>
        </motion.div>
      )}
      {guide && <IosSteps key="guide" onClose={() => { setGuide(false); dismiss(); }} />}
    </AnimatePresence>
  );
}
